import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import {
  Code,
  DataObject,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  HorizontalRule,
  Link as LinkIcon,
  LinkOff,
  Redo,
  StrikethroughS,
  Undo,
} from '@mui/icons-material';
import { Editor, useEditorState } from '@tiptap/react';
import { useState } from 'react';
import LinkEditorModal from 'components/LinkEditorModal';

interface Props {
  editor: Editor;
}

const ToolbarButton: React.FunctionComponent<{
  title: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, active = false, disabled = false, children }) => (
  <Tooltip title={title} placement="top">
    <span>
      <IconButton
        size="small"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderRadius: 1,
          p: '4px',
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          '&.Mui-disabled': { opacity: 0.3 },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const HeadingButton: React.FunctionComponent<{
  level: 1 | 2 | 3;
  active: boolean;
  onClick: () => void;
}> = ({ level, active, onClick }) => (
  <Tooltip title={`Heading ${level}`} placement="top">
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        borderRadius: 1,
        p: '2px 4px',
        fontWeight: 700,
        fontSize: '0.7rem',
        lineHeight: 1,
        minWidth: 24,
        color: active ? 'primary.main' : 'text.secondary',
        bgcolor: active ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      H{level}
    </IconButton>
  </Tooltip>
);

const ToolbarDivider = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />;

const EditorContentMenuBar: React.FunctionComponent<Props> = ({ editor }) => {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkInitialUrl, setLinkInitialUrl] = useState('');
  // Opening the modal traps focus, blurring the editor and losing the live
  // selection. Capture the range when the modal opens so Apply/Remove can
  // restore it and always operate on the text the author highlighted.
  const [linkRange, setLinkRange] = useState<{ from: number; to: number } | null>(null);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor.isActive('bold'),
      canBold: ctx.editor.can().chain().toggleBold().run(),
      isItalic: ctx.editor.isActive('italic'),
      canItalic: ctx.editor.can().chain().toggleItalic().run(),
      isStrike: ctx.editor.isActive('strike'),
      canStrike: ctx.editor.can().chain().toggleStrike().run(),
      isCode: ctx.editor.isActive('code'),
      canCode: ctx.editor.can().chain().toggleCode().run(),
      isH1: ctx.editor.isActive('heading', { level: 1 }),
      isH2: ctx.editor.isActive('heading', { level: 2 }),
      isH3: ctx.editor.isActive('heading', { level: 3 }),
      isBulletList: ctx.editor.isActive('bulletList'),
      isOrderedList: ctx.editor.isActive('orderedList'),
      isCodeBlock: ctx.editor.isActive('codeBlock'),
      isBlockquote: ctx.editor.isActive('blockquote'),
      canUndo: ctx.editor.can().chain().undo().run(),
      canRedo: ctx.editor.can().chain().redo().run(),
      isLink: ctx.editor.isActive('link'),
      hasSelection: !ctx.editor.state.selection.empty,
      isEditable: ctx.editor.isEditable,
    }),
  });

  const openLinkModal = () => {
    const { from, to } = editor.state.selection;
    setLinkRange({ from, to });
    setLinkInitialUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkModalOpen(true);
  };

  const closeLinkModal = () => {
    setLinkModalOpen(false);
    setLinkInitialUrl('');
    setLinkRange(null);
  };

  const applyLink = (href: string) => {
    if (!linkRange) {
      closeLinkModal();
      return;
    }
    const chain = editor.chain().focus().setTextSelection(linkRange).extendMarkRange('link');
    if (!href) {
      // Empty input clears the link rather than creating an invalid one.
      chain.unsetLink().run();
    } else {
      chain.setLink({ href }).run();
    }
    closeLinkModal();
  };

  const removeLink = () => {
    if (linkRange) {
      editor.chain().focus().setTextSelection(linkRange).extendMarkRange('link').unsetLink().run();
    }
    closeLinkModal();
  };

  return (
    <Box
      // The toolbar sits inside LegalEditor's editor surface, whose onClick
      // refocuses the editor. React bubbles clicks from the portaled modal
      // through this subtree too, so stop propagation here to keep focus (and
      // keystrokes) in the toolbar/modal instead of being pulled into the editor.
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.25,
        p: 0.5,
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: '4px 4px 0 0',
      }}
    >
      {/* Inline formatting */}
      <ToolbarButton
        title="Bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editorState.isBold}
        disabled={!editorState.canBold}
      >
        <FormatBold fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editorState.isItalic}
        disabled={!editorState.canItalic}
      >
        <FormatItalic fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editorState.isStrike}
        disabled={!editorState.canStrike}
      >
        <StrikethroughS fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Inline code"
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editorState.isCode}
        disabled={!editorState.canCode}
      >
        <Code fontSize="small" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Links */}
      <ToolbarButton
        title="Link"
        onClick={openLinkModal}
        active={editorState.isLink}
        disabled={!editorState.isEditable || (!editorState.hasSelection && !editorState.isLink)}
      >
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Unlink"
        onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
        active={editorState.isLink}
        disabled={!editorState.isEditable || !editorState.isLink}
      >
        <LinkOff fontSize="small" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings */}
      <HeadingButton
        level={1}
        active={editorState.isH1}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <HeadingButton
        level={2}
        active={editorState.isH2}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <HeadingButton
        level={3}
        active={editorState.isH3}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        title="Bullet list"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editorState.isBulletList}
      >
        <FormatListBulleted fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Ordered list"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editorState.isOrderedList}
      >
        <FormatListNumbered fontSize="small" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block formatting */}
      <ToolbarButton
        title="Blockquote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editorState.isBlockquote}
      >
        <FormatQuote fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Code block"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={editorState.isCodeBlock}
      >
        <DataObject fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <HorizontalRule fontSize="small" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* History */}
      <ToolbarButton
        title="Undo"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editorState.canUndo}
      >
        <Undo fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editorState.canRedo}
      >
        <Redo fontSize="small" />
      </ToolbarButton>

      <LinkEditorModal
        open={linkModalOpen}
        isEditing={editorState.isLink}
        initialUrl={linkInitialUrl}
        onApply={applyLink}
        onRemove={removeLink}
        onClose={closeLinkModal}
      />
    </Box>
  );
};

export default EditorContentMenuBar;
